/**
 * Privacy Policy — /privacy
 *
 * Content is REAL — accurate to what the platform actually does. Every claim
 * below maps to a real implementation:
 *   - Soft-delete + 14-day grace: iter85 (Item S) account_lifecycle.py
 *   - 90-day telemetry prune: iter85 hygiene loop
 *   - page_events aggregate-only: iter83 Wave 2 admin_analytics
 *   - Resend email: emailer.py
 *   - MongoDB storage: primary datastore
 * Marked as final at the top - published on 2026-07-28 per founder decision.
 */
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-paper">
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="privacy-page">
        <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Ministry of Exchange</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-ink">Privacy Policy</h1>
        <p className="mt-3 text-sm text-muted" data-testid="privacy-last-updated">
          Last updated: 2 August 2026 · session cookie disclosure, legal-disclosure section, third-party links
        </p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-ink/85 [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-ink [&_p]:mb-3 [&_ul]:mb-3 [&_ul]:ml-5 [&_ul]:list-disc [&_li]:mb-1">

          <section>
            <h2>1. Who we are</h2>
            <p>
              Ministry of Exchange is operated by Nikshala AI, the parent company, with Ministry of Exchange
              operating as a sister product under the Nikshala AI family. Contact for privacy questions:
              {" "}<a href="mailto:nd@nikshala.com" className="text-elec font-semibold underline">nd@nikshala.com</a>.
            </p>
          </section>

          <section>
            <h2>2. What data we collect</h2>
            <ul>
              <li><b>Account information:</b> name, email, hashed password. Password is stored hashed via bcrypt; we never see or store your plaintext password.</li>
              <li><b>Case &amp; visa details:</b> the country, host university, host city, home university, home city, duration, start date, and process stage you enter into Northstar. Optional: persona questionnaire answers (traveler type, primary purpose, budget tier, cohort visibility choice).</li>
              <li><b>WhatsApp number:</b> collected as a required field at signup so accepted matches can contact you off-platform. Same privacy gating as before — WhatsApp is <b>only revealed to peers with whom you have a mutually-accepted connection</b>. It is never displayed on match cards, cohort feeds, discovery surfaces, or any unauthenticated context.</li>
              <li><b>Cohort profile:</b> if you opt in to peer matching, your first name / chosen display name, host city, program, and a short bio you write yourself.</li>
              <li><b>Messages:</b> direct messages and cohort group messages you send through the Platform, stored to enable the messaging feature to function. We do not have standing access to read your private conversations. If you or another user reports a specific conversation for a safety concern (e.g. harassment, spam, impersonation), that specific reported conversation becomes visible to our admin team for review, and only after a report has been filed. We log every admin review of a reported conversation.</li>
              <li><b>Assembly coordination data:</b> trips, arrivals, roommate posts, and social plans you create or join.</li>
              <li><b>Telemetry:</b> aggregate page-view events (path, device class, timestamp) to understand which products are being used. We do NOT track individual keystrokes, mouse movement, or session recordings.</li>
              <li><b>Documents:</b> if you attach a document URL to your case (e.g. a PDF you host elsewhere), we validate the URL is a normal http/https address to a public host and store the URL. We do not fetch or store the file itself.</li>
            </ul>
          </section>

          <section>
            <h2>3. Why we collect it</h2>
            <p>To provide the service — build your visa timeline, match you with peers, coordinate group plans, and give you an accurate packing list. Telemetry is used only in aggregate to improve which products get built next.</p>
          </section>

          <section>
            <h2>4. What we don&rsquo;t do</h2>
            <ul>
              <li>We do not sell, rent, or trade your personal data to third parties. Ever.</li>
              <li>We do not read your private messages by default &mdash; only a specifically reported conversation becomes visible to our admin team, and only after a report is filed.</li>
            </ul>
          </section>

          <section>
            <h2>5. GDPR rights (EU/EEA users)</h2>
            <ul>
              <li><b>Access:</b> you can see all of your data in-product — your case, Cohort profile, messages, and Assembly participation are all readable to you within the app.</li>
              <li><b>Correction:</b> you can edit your case, profile, and Cohort details at any time from the relevant pages.</li>
              <li><b>Deletion:</b> you can delete your account from the Dashboard. Deletion is a soft-delete with a 14-day reversible grace period, after which all your personal data is permanently removed by our automated hygiene process. Cohort messages you sent are anonymized so remaining participants can still read the thread coherently, but nothing identifies you.</li>
              <li><b>Portability:</b> for a portable export of your data in a machine-readable format, email <a href="mailto:nd@nikshala.com" className="text-elec font-semibold underline">nd@nikshala.com</a>.</li>
            </ul>
          </section>

          <section>
            <h2>6. Data retention</h2>
            <ul>
              <li><b>Active accounts:</b> we retain your data as long as your account is active and for as long as needed to provide the service.</li>
              <li><b>Deleted accounts:</b> 14-day soft-delete grace period, then permanent removal by our automated hygiene job.</li>
              <li><b>Telemetry (page_events):</b> aggregate page-view events are automatically pruned after 90 days by the same hygiene job.</li>
              <li><b>Login attempts:</b> login-attempt records used for brute-force protection are pruned after 30 days.</li>
            </ul>
          </section>

          <section>
            <h2>7. Third-party processors</h2>
            <p>We use the following third parties to run the service. Each is bound by their own privacy terms:</p>
            <ul>
              <li><b>MongoDB Atlas</b> — primary data storage (accounts, cases, messages, telemetry).</li>
              <li><b>Resend</b> — transactional email delivery (welcome, match, request, and Concierge confirmation emails). Resend receives only the recipient email address and the email content we send.</li>
              <li><b>Frankfurter.app</b> — currency conversion (Northstar visa fees, Cost Estimator). Frankfurter receives no user data - only currency pair queries.</li>
              <li><b>Emergent Labs</b> — hosting and deployment infrastructure.</li>
            </ul>
          </section>

          <section>
            <h2>8. Third-party links</h2>
            <p>
              The Platform links to third-party services for travel search (e.g. flight and bus search
              providers such as Ryanair, FlixBus, and Omio). We are not responsible for the privacy
              practices, security, or content of these external sites. Once you leave our Platform via
              one of these links, their own privacy policy and terms apply, not ours.
            </p>
          </section>

          <section>
            <h2>9. Legal disclosure</h2>
            <p>
              In addition to the uses described above, we may disclose information where required to do
              so by law, a valid court order, or a legally binding request from a competent authority.
              We do not disclose data to law enforcement or any third party beyond what is legally
              required.
            </p>
          </section>

          <section>
            <h2>10. Cookies and tracking</h2>
            <p>
              We use a session cookie/token to keep you logged in between visits. This is required for
              the Platform to function and cannot be disabled while remaining logged in. Beyond this,
              our aggregate usage tracking (described above) does not use third-party advertising
              cookies.
            </p>
            <p>
              Technical detail for the curious: the login token is an httpOnly, SameSite=Lax cookie. We
              do not run advertising cookies, cross-site trackers, or third-party analytics scripts
              like Meta Pixel. The aggregate page-view telemetry we do collect runs entirely on our own
              backend and never leaves our servers, with one narrow exception: we send anonymous
              pageview signals to Google Analytics 4 (measurement ID G-4WYTMH56W7) so we can see which
              products are getting used at a funnel level. GA4 receives page paths and a per-browser
              client ID; we do not send it your name, email, WhatsApp, messages, or any case detail.
            </p>
          </section>

          <section>
            <h2>11. Security</h2>
            <ul>
              <li>Passwords are stored as bcrypt hashes.</li>
              <li>Authentication uses JWT (HS256) with an httpOnly cookie fallback.</li>
              <li>Login attempts are rate-limited to protect against brute force.</li>
              <li>User-supplied URLs (e.g. document attachments) are validated to block internal IP ranges and non-http(s) schemes.</li>
            </ul>
          </section>

          <section>
            <h2>12. Children</h2>
            <p>
              The Platform is for users aged 18 and above. We do not knowingly collect data from children under 18.
              If you believe a child under 18 has created an account, email us and we will delete it.
            </p>
          </section>

          <section>
            <h2>13. Changes to this policy</h2>
            <p>
              We may update this policy. We will make reasonable efforts to notify active users of material changes.
            </p>
          </section>

          <section>
            <h2>14. Contact</h2>
            <p>
              For any privacy question — access, correction, deletion, portability, or general concern — email
              {" "}<a href="mailto:nd@nikshala.com" className="text-elec font-semibold underline">nd@nikshala.com</a>
              {" "}or call <a href="tel:+918263907201" className="text-elec font-semibold underline">+91 82639 07201</a>.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
